import type { ThemeDef } from '@/constants/themes';

/**
 * Pure preset-resolution helpers (no MUI, no React). Extracted from the old
 * MUI `theme.ts` so the app's theming no longer depends on `@mui/material`.
 *
 * The header light/dark toggle is global and authoritative. When the user picks
 * a mode the active preset doesn't natively provide (e.g. dark mode on a light
 * default), `resolvePresetForMode` derives a same-brand variant: keep the
 * preset's identity + brand hue, swap the chrome to the target mode's neutrals,
 * and re-tune the brand so it stays legible against the new surface (lighten for
 * dark, darken for light). Returns the preset untouched when its mode matches.
 */

const parseHex = (hex: string) => {
  const h = hex.replace('#', '');
  return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)];
};

const toHex = (r: number, g: number, b: number) =>
  `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;

export const lightenColor = (color: string, amount: number) => {
  const [r, g, b] = parseHex(color);
  return toHex(
    Math.min(255, Math.floor(r + (255 - r) * amount)),
    Math.min(255, Math.floor(g + (255 - g) * amount)),
    Math.min(255, Math.floor(b + (255 - b) * amount)),
  );
};

export const darkenColor = (color: string, amount: number) => {
  const [r, g, b] = parseHex(color);
  return toHex(
    Math.max(0, Math.floor(r * (1 - amount))),
    Math.max(0, Math.floor(g * (1 - amount))),
    Math.max(0, Math.floor(b * (1 - amount))),
  );
};

// Neutral chrome ramps used when the global light/dark toggle asks for a mode
// the active preset doesn't natively ship. Only the surfaces/ink swap — the
// preset's brand hue is preserved (and re-tuned for legibility) below.
const DARK_NEUTRALS = { bg: '#0B0C10', surface: '#15171E', surfaceAlt: '#1D2029', border: '#2C2F3A', text: '#F1F3F6', muted: '#9BA3B0' };
const LIGHT_NEUTRALS = { bg: '#F8FAFC', surface: '#FFFFFF', surfaceAlt: '#F1F5F9', border: '#E2E8F0', text: '#0F172A', muted: '#64748B' };

export function resolvePresetForMode(preset: ThemeDef, mode: 'light' | 'dark'): ThemeDef {
  if (preset.mode === mode) return preset;
  const neutrals = mode === 'dark' ? DARK_NEUTRALS : LIGHT_NEUTRALS;
  return {
    ...preset,
    mode,
    ...neutrals,
    primary: mode === 'dark' ? lightenColor(preset.primary, 0.18) : darkenColor(preset.primary, 0.12),
    primaryDark: preset.primary,
    accent: mode === 'dark' ? lightenColor(preset.accent, 0.18) : darkenColor(preset.accent, 0.12),
  };
}
